import React from "react";
import { useTranslation } from "react-i18next";
import { INTERFACES, gradeOf, qualityColor } from "../../../utils/netquality";
import styles from "../network_map.module.css";

const PERIOD_KEY = { 1: "day", 7: "week", 30: "month" };

function Delta({ row, t, compact = false }) {
  if (!row || row.score === null || row.score === undefined) return null;
  if (row.prev_score === null || row.prev_score === undefined) {
    if (compact) return null;
    return <span className={`${styles.kpiDelta} ${styles.kpiDeltaNeutral}`}>{t("netmap.kpi.noPrevious")}</span>;
  }
  const d = row.delta ?? 0;
  if (Math.abs(d) < 1) {
    return <span className={`${styles.kpiDelta} ${styles.kpiDeltaNeutral}`}>{compact ? "=" : t("netmap.kpi.flat")}</span>;
  }
  // Más puntaje es mejor: subir es verde, bajar es rojo. Flecha + texto, nunca solo color.
  const up = d > 0;
  return (
    <span className={`${styles.kpiDelta} ${up ? styles.kpiDeltaUp : styles.kpiDeltaDown}`}>
      {up ? "▲" : "▼"} {up ? "+" : "−"}{Math.abs(Math.round(d))}{compact ? "" : ` ${t("netmap.kpi.vsPrevious")}`}
    </span>
  );
}

/**
 * Indicador de flota: calidad promedio de todas las mediciones de los equipos
 * seleccionados en el período, para la red elegida, con el cambio frente al
 * período anterior de igual duración, y las otras dos redes como accesos.
 */
export default function KpiStrip({ summary, iface, setIface, days }) {
  const { t } = useTranslation();
  const rows = summary?.interfaces || {};
  const hasAny = INTERFACES.some((k) => (rows[k]?.n || 0) > 0);
  if (!hasAny) return null;
  const main = rows[iface] || {};
  const periodLabel = t(`netmap.periods.${PERIOD_KEY[days] || "week"}`);
  const score = main.score;
  const others = INTERFACES.filter((k) => k !== iface);
  return (
    <div className={styles.kpi} role="group" aria-label={t("netmap.kpi.title")}>
      <div className={styles.kpiHero}>
        <div className={styles.kpiLabel}>{t("netmap.kpi.title")} · {t(`netmap.iface.${iface}`)} · {periodLabel}</div>
        <div className={styles.kpiValueRow}>
          {score !== null && score !== undefined ? (
            <>
              <span className={styles.kpiDot} style={{ background: qualityColor(score) }} aria-hidden="true" />
              <span className={styles.kpiValue}>{Math.round(score)}</span>
              <span className={styles.kpiGrade}>{gradeOf(score)}</span>
              <Delta row={main} t={t} />
            </>
          ) : (
            <span className={styles.kpiEmpty}>{t("netmap.kpi.noData")}</span>
          )}
        </div>
        {main.n > 0 && (
          <div className={styles.kpiSub}>
            {t("netmap.kpi.samples", { n: (main.n || 0).toLocaleString() })} · {t("netmap.kpi.devices", { n: main.devices || 0 })}
          </div>
        )}
      </div>
      {others.map((k) => {
        const row = rows[k] || {};
        const has = row.score !== null && row.score !== undefined;
        return (
          <button
            key={k}
            type="button"
            className={styles.kpiMini}
            onClick={() => setIface(k)}
            title={t("netmap.kpi.switchTo", { iface: t(`netmap.iface.${k}`) })}
          >
            <div className={styles.kpiLabel}>{t(`netmap.iface.${k}`)}</div>
            <div className={styles.kpiMiniValue}>
              {has ? (
                <>
                  <span className={styles.kpiDotSmall} style={{ background: qualityColor(row.score) }} aria-hidden="true" />
                  {Math.round(row.score)}
                  <Delta row={row} t={t} compact />
                </>
              ) : (
                <span className={styles.kpiEmpty}>—</span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
